import { useState, useEffect } from 'react'
import { useFinancialPlanStore, RiskProfile } from '../../store/financialPlanStore'
import { Shield, AlertCircle } from 'lucide-react'

const RISK_QUESTIONS = [
  {
    id: 1,
    question: 'How would you describe your investment knowledge?',
    options: [
      { text: 'Limited - I\'m new to investing', score: 1 },
      { text: 'Basic - I understand fundamentals', score: 2 },
      { text: 'Intermediate - I\'ve invested for several years', score: 3 },
      { text: 'Advanced - I have extensive experience', score: 4 },
      { text: 'Expert - I could teach others', score: 5 },
    ],
  },
  {
    id: 2,
    question: 'If your portfolio dropped 20% in a year, what would you do?',
    options: [
      { text: 'Sell everything and move to cash', score: 1 },
      { text: 'Sell some and reduce risk', score: 2 },
      { text: 'Hold steady and wait', score: 3 },
      { text: 'Hold steady and possibly buy more', score: 4 },
      { text: 'Buy aggressively - it\'s on sale!', score: 5 },
    ],
  },
  {
    id: 3,
    question: 'What is your primary investment goal?',
    options: [
      { text: 'Preserve capital - avoid losses', score: 1 },
      { text: 'Generate income with stability', score: 2 },
      { text: 'Balance growth and income', score: 3 },
      { text: 'Long-term growth', score: 4 },
      { text: 'Maximum growth - willing to take big risks', score: 5 },
    ],
  },
  {
    id: 4,
    question: 'How many years until you need this money?',
    options: [
      { text: 'Less than 3 years', score: 1 },
      { text: '3-7 years', score: 2 },
      { text: '8-15 years', score: 3 },
      { text: '16-25 years', score: 4 },
      { text: 'More than 25 years', score: 5 },
    ],
  },
  {
    id: 5,
    question: 'Do you have an emergency fund covering 6+ months of expenses?',
    options: [
      { text: 'No emergency fund', score: 1 },
      { text: '1-3 months saved', score: 2 },
      { text: '3-6 months saved', score: 3 },
      { text: '6-12 months saved', score: 4 },
      { text: 'Over 12 months saved', score: 5 },
    ],
  },
]

export function RiskProfileStep() {
  const { currentPlan, setRiskProfile, setRiskAnswers } = useFinancialPlanStore()
  const [answers, setAnswers] = useState<{ [key: number]: number }>(currentPlan?.riskAnswers || {})
  const [showResults, setShowResults] = useState(false)
  
  useEffect(() => {
    setRiskAnswers(answers)
  }, [answers, setRiskAnswers])
  
  if (!currentPlan) return null
  
  const handleAnswer = (questionId: number, score: number) => {
    setAnswers({ ...answers, [questionId]: score })
  }
  
  const calculateRiskProfile = () => {
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0)
    const avgScore = totalScore / RISK_QUESTIONS.length
    
    let profile: RiskProfile
    
    if (avgScore <= 1.5) {
      profile = {
        score: Math.round(avgScore * 10) / 10,
        tolerance: 'Conservative',
        stocksPercent: 30,
        bondsPercent: 60,
      }
    } else if (avgScore <= 2.5) {
      profile = {
        score: Math.round(avgScore * 10) / 10,
        tolerance: 'Moderate Conservative',
        stocksPercent: 50,
        bondsPercent: 40,
      }
    } else if (avgScore <= 3.5) {
      profile = {
        score: Math.round(avgScore * 10) / 10,
        tolerance: 'Moderate',
        stocksPercent: 65,
        bondsPercent: 30,
      }
    } else if (avgScore <= 4.5) {
      profile = {
        score: Math.round(avgScore * 10) / 10,
        tolerance: 'Moderate Aggressive',
        stocksPercent: 80,
        bondsPercent: 15,
      }
    } else {
      profile = {
        score: Math.round(avgScore * 10) / 10,
        tolerance: 'Aggressive',
        stocksPercent: 90,
        bondsPercent: 5,
      }
    }
    
    setRiskProfile(profile)
    setShowResults(true)
  }
  
  const allQuestionsAnswered = Object.keys(answers).length === RISK_QUESTIONS.length
  
  const getRiskColor = (tolerance: string) => {
    switch (tolerance) {
      case 'Conservative': return 'text-blue-600 bg-blue-50'
      case 'Moderate Conservative': return 'text-green-600 bg-green-50'
      case 'Moderate': return 'text-yellow-600 bg-yellow-50'
      case 'Moderate Aggressive': return 'text-orange-600 bg-orange-50'
      case 'Aggressive': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }
  
  if (showResults && currentPlan.riskProfile) {
    const profile = currentPlan.riskProfile
    
    return (
      <div className="space-y-6">
        <div className={`rounded-lg p-6 ${getRiskColor(profile.tolerance)}`}>
          <div className="flex items-center mb-4">
            <Shield className="h-8 w-8 mr-3" />
            <div>
              <h3 className="text-2xl font-bold">{profile.tolerance}</h3>
              <p className="text-sm">Risk Tolerance Profile</p>
            </div>
          </div>
          
          <div className="mt-4">
            <p className="font-medium mb-2">Recommended Asset Allocation:</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm">Stocks</p>
                <p className="text-3xl font-bold">{profile.stocksPercent}%</p>
              </div>
              <div>
                <p className="text-sm">Bonds</p>
                <p className="text-3xl font-bold">{profile.bondsPercent}%</p>
              </div>
              <div>
                <p className="text-sm">Cash</p>
                <p className="text-3xl font-bold">{100 - profile.stocksPercent - profile.bondsPercent}%</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">What This Means</h4>
              <p className="text-sm text-blue-700">
                {profile.tolerance === 'Conservative' && 'You prefer stability and capital preservation. Lower volatility with modest growth potential.'}
                {profile.tolerance === 'Moderate Conservative' && 'You seek balance with slight emphasis on stability. Moderate growth with limited volatility.'}
                {profile.tolerance === 'Moderate' && 'You balance growth and stability equally. This is the "60/40" approach many investors use.'}
                {profile.tolerance === 'Moderate Aggressive' && 'You prioritize growth while accepting higher volatility. Suitable for long time horizons.'}
                {profile.tolerance === 'Aggressive' && 'You maximize growth potential and accept significant short-term volatility for long-term gains.'}
              </p>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => {
            setShowResults(false)
            setAnswers({})
            setRiskProfile(null as any)
          }}
          className="btn-secondary w-full"
        >
          Retake Assessment
        </button>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">Risk Tolerance Assessment</h3>
            <p className="text-sm text-blue-700">
              Answer these questions honestly to determine your investment risk profile. There are no right or wrong answers.
            </p>
          </div>
        </div>
      </div>
      
      {RISK_QUESTIONS.map((question) => (
        <div key={question.id} className="card">
          <h4 className="font-medium mb-4">
            {question.id}. {question.question}
          </h4>
          <div className="space-y-2">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(question.id, option.score)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  answers[question.id] === option.score
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                {option.text}
              </button>
            ))}
          </div>
        </div>
      ))}
      
      <button
        onClick={calculateRiskProfile}
        disabled={!allQuestionsAnswered}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {allQuestionsAnswered ? 'Calculate Risk Profile' : `Answer All Questions (${Object.keys(answers).length}/${RISK_QUESTIONS.length})`}
      </button>
    </div>
  )
}
